import React, { useState } from 'react';
import { HelpCircle, MessageSquare, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: '¿Puedo jugar completamente en solitario o necesito otros jugadores?', acceptedAnswer: { '@type': 'Answer', text: 'Puedes jugar 100% en solitario gracias al sistema Warband: hasta 4 compañeros IA con specs reales que te acompañan en mazmorras, raids y Mythic+.' } },
    { '@type': 'Question', name: '¿Qué hace diferente a Frostbound de otros servidores WotLK?', acceptedAnswer: { '@type': 'Answer', text: 'Warband (compañeros IA), Mythic+ (mazmorras escalables con 7 niveles y afijos) y Housing (casas de jugador). Todo sobre AzerothCore con el contenido WotLK íntegro.' } },
    { '@type': 'Question', name: '¿Cuáles son las tasas del servidor?', acceptedAnswer: { '@type': 'Answer', text: 'XP x1 a x7 ajustable individualmente con .xp set X. Botín x2 y oro x2 global.' } },
    { '@type': 'Question', name: '¿La tienda es pay-to-win?', acceptedAnswer: { '@type': 'Answer', text: 'No. Solo cosméticos y servicios de comodidad. Ningún artículo otorga ventaja en combate PvE ni PvP.' } },
    { '@type': 'Question', name: '¿Qué contenido PvE hay disponible?', acceptedAnswer: { '@type': 'Answer', text: 'Todo el contenido WotLK: Naxxramas, Ulduar, Trial of the Crusader, Icecrown Citadel y Ruby Sanctum. Además Mythic+ con 14 mazmorras escalables de M+1 a M+7.' } },
    { '@type': 'Question', name: '¿Hay PvP Cross-Faction?', acceptedAnswer: { '@type': 'Answer', text: 'Sí. Cross-Faction BG permite battlegrounds y mazmorras con jugadores de ambas facciones mezclados.' } },
    { '@type': 'Question', name: '¿Hay transmogrifación?', acceptedAnswer: { '@type': 'Answer', text: 'Sí. El NPC Transmogrificador está disponible en las ciudades principales.' } },
    { '@type': 'Question', name: '¿Qué versión del cliente necesito y cómo me conecto?', acceptedAnswer: { '@type': 'Answer', text: 'WoW 3.3.5a (build 12340). Edita realmlist.wtf con: set realmlist logon.frostbound.org' } },
    { '@type': 'Question', name: '¿Están permitidos los addons?', acceptedAnswer: { '@type': 'Answer', text: 'Sí, son compatibles todos los addons diseñados para WoW 3.3.5a sin restricciones. Recomendamos encarecidamente MultiBots (en nuestra sección de Descargas): imprescindible para el sistema Warband.' } },
    { '@type': 'Question', name: '¿Cómo reporto un bug o a un jugador infractor?', acceptedAnswer: { '@type': 'Answer', text: 'Ticket en Discord (#soporte) o comando .ticket en el juego. Bugs críticos: admin@frostbound.org.' } },
  ],
};

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`faq-item ${open ? 'faq-item--open' : ''}`}
      data-testid={`faq-item-${item.id}`}
    >
      <button
        className="faq-question"
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${item.id}`}
      >
        <span>{item.q}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="faq-answer" data-testid={`faq-answer-${item.id}`}>
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function SoportePage() {
  const { t } = useTranslation();
  const faqs = t('support.faqs', { returnObjects: true });

  return (
    <PageWrapper scrollable>
      <SEOMeta
        title="Soporte | FAQ y ayuda técnica"
        description="Resuelve problemas de instalación, realmlist, cuenta y addons con la guía oficial de soporte de Frostbound."
        canonical="/soporte"
        jsonLd={FAQ_SCHEMA}
      />
      <Navbar />

      <main className="soporte-main">
        <div className="dl-header anim-fade-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="shop-title" data-testid="soporte-heading">
            <HelpCircle size={30} className="shop-title-icon" />
            {t('support.title')}
          </h1>
          <p className="shop-subtitle">
            {t('support.subtitle_pre')}{' '}
            <span className="cyan-highlight">{t('support.subtitle_hl')}</span>
          </p>
        </div>

        <div className="discord-banner anim-fade-up" style={{ animationDelay: '0.2s' }} data-testid="discord-banner">
          <MessageSquare size={26} className="discord-icon" />
          <div className="discord-text">
            <p className="discord-title">{t('support.discord_title')}</p>
            <p className="discord-desc">{t('support.discord_desc')}</p>
          </div>
          <a
            href="https://discord.com/invite/RwpMfRdzex"
            className="discord-btn"
            data-testid="discord-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={14} />
            {t('support.discord_btn')}
          </a>
        </div>

        <div className="faq-section anim-fade-up" style={{ animationDelay: '0.3s' }}>
          <h2 className="faq-title" data-testid="faq-section-title">
            {t('support.faq_title')}
          </h2>
          <div className="faq-list" data-testid="faq-list">
            {faqs.map((item) => (
              <FaqItem key={item.id} item={item} />
            ))}
          </div>
        </div>

        <div className="soporte-notice anim-fade-up" style={{ animationDelay: '0.4s' }}>
          <p>
            {t('support.contact_notice')}{' '}
            <span className="cyan-highlight" style={{ fontSize: 'inherit' }}>admin@frostbound.org </span>
          </p>
        </div>
      </main>

      <Footer />
    </PageWrapper>
  );
}
